import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ChapterIcon } from './Illustrations';

// A long section folds away until opened. Phones show a "chapter card"
// (photo, title, one line) in its place; desktop uses ChapterIndex instead.
// The section stays in the page (with its id), just collapsed to zero height,
// so navigation and in-page links keep working: anything that points inside a
// folded chapter opens it first.

interface ChapterFoldProps {
  id: string;
  number: string;
  title: string;
  summary: string;
  meta: string;
  children: React.ReactNode;
}

export const OPEN_CHAPTER_EVENT = 'corazon:open-chapter';
export const TOGGLE_CHAPTER_EVENT = 'corazon:toggle-chapter';
export const CHAPTER_STATE_EVENT = 'corazon:chapter-state';
// Only one chapter is open at a time: opening one collapses the one that was
// open before (synchronously, so scroll positions measured right after are final).
const CHAPTER_SOLO_EVENT = 'corazon:chapter-solo';
const closeOthers = (id: string) => window.dispatchEvent(new CustomEvent(CHAPTER_SOLO_EVENT, { detail: { id } }));

// Events from other sections that should reveal a chapter.
const EVENT_TARGETS: Record<string, string> = {
  'corazon:open-restaurant': 'dining',
  'corazon:open-attraction': 'neighborhoods-explore',
  'corazon:open-area': 'neighborhoods-explore',
  'corazon:open-event': 'climate',
};

const ChapterFold: React.FC<ChapterFoldProps> = ({ id, number, title, summary, meta, children }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const openRef = useRef(false);
  openRef.current = open;

  const reveal = useCallback(() => {
    if (!openRef.current) closeOthers(id);
    flushSync(() => setOpen(true));
  }, [id]);

  useEffect(() => {
    const containsTarget = (selector: string) => {
      try {
        const el = document.querySelector(selector);
        return !!el && !!wrapRef.current?.contains(el);
      } catch {
        return false;
      }
    };

    // In-page links (#dining, #parking, …) — open before the scroll runs.
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.('a[href^="#"]') as HTMLAnchorElement | null;
      const href = anchor?.getAttribute('href');
      if (!href || href === '#') return;
      if (anchor && wrapRef.current?.contains(anchor)) return;
      if (containsTarget(href)) reveal();
    };
    const onOpenChapter = (event: Event) => {
      if ((event as CustomEvent<{ id: string }>).detail?.id === id) reveal();
    };
    const onToggleChapter = (event: Event) => {
      if ((event as CustomEvent<{ id: string }>).detail?.id !== id) return;
      if (openRef.current) flushSync(() => setOpen(false));
      else reveal();
    };
    const onSolo = (event: Event) => {
      if ((event as CustomEvent<{ id: string }>).detail?.id !== id && openRef.current) flushSync(() => setOpen(false));
    };
    const onNamed = (event: Event) => {
      if (EVENT_TARGETS[event.type] === id) reveal();
    };

    document.addEventListener('click', onClick, true);
    window.addEventListener(OPEN_CHAPTER_EVENT, onOpenChapter);
    window.addEventListener(TOGGLE_CHAPTER_EVENT, onToggleChapter);
    window.addEventListener(CHAPTER_SOLO_EVENT, onSolo);
    Object.keys(EVENT_TARGETS).forEach((name) => window.addEventListener(name, onNamed));
    if (window.location.hash && containsTarget(window.location.hash)) setOpen(true);

    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener(OPEN_CHAPTER_EVENT, onOpenChapter);
      window.removeEventListener(TOGGLE_CHAPTER_EVENT, onToggleChapter);
      window.removeEventListener(CHAPTER_SOLO_EVENT, onSolo);
      Object.keys(EVENT_TARGETS).forEach((name) => window.removeEventListener(name, onNamed));
    };
  }, [id, reveal]);

  // Let the desktop chapter index know which chapters are open.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent(CHAPTER_STATE_EVENT, { detail: { id, open } }));
  }, [id, open]);

  return (
    <div ref={wrapRef} className={`chapter-fold ${open ? 'is-open' : ''}`} data-chapter={id}>
      <button
        type="button"
        className="chapter-card"
        aria-expanded={open}
        aria-controls={id}
        onClick={(event) => {
          if (openRef.current) {
            flushSync(() => setOpen(false));
            return;
          }
          // Keep the tapped card where it is while chapters above it fold away.
          const card = event.currentTarget;
          const before = card.getBoundingClientRect().top;
          reveal();
          const shift = card.getBoundingClientRect().top - before;
          if (Math.abs(shift) > 1) window.scrollBy({ top: shift, behavior: 'instant' as ScrollBehavior });
        }}
      >
        <span className="chapter-card-badge"><ChapterIcon id={id} /></span>
        <span className="chapter-card-text">
          <small>{number} · {meta}</small>
          <strong>{title}</strong>
          <span>{summary}</span>
        </span>
        <span className="chapter-card-toggle" aria-hidden="true">{open ? '–' : '+'}</span>
      </button>
      {children}
    </div>
  );
};

export default ChapterFold;
